import { Trash2, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { User } from '@/services/api';
import { useState } from 'react';

interface UserListProps {
  users: Record<string, User>;
  onDeleteUser: (userId: string) => void;
  isDeleting?: boolean;
}

export function UserList({ users, onDeleteUser, isDeleting }: UserListProps) {
  const { t } = useTranslation();
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const sortedUsers = Object.values(users).sort((a, b) => {
    // Sort pre-created users first, then by name
    if (a.status === 'pre-created' && b.status !== 'pre-created') return -1;
    if (a.status !== 'pre-created' && b.status === 'pre-created') return 1;

    return (a.name || a.email || '').localeCompare(b.name || b.email || '');
  });

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      onDeleteUser(userToDelete.id);
      setUserToDelete(null);
    }
  };

  const isPreCreated = (user: User) => user.status === 'pre-created';

  return (
    <>
      <div className="space-y-3">
        {sortedUsers.map((user) => (
          <Card key={user.id}>
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {user.image ? (
                  <img src={user.image} alt={user.name || ''} className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <UserIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{user.name || user.email || user.id}</span>
                    {isPreCreated(user) ? (
                      <Badge variant="secondary">{t('users.pending')}</Badge>
                    ) : (
                      <Badge variant="default">{t('users.active')}</Badge>
                    )}
                  </div>
                  {user.name && user.email && <span className="text-sm text-muted-foreground">{user.email}</span>}
                </div>
              </div>

              {isPreCreated(user) && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(user)}
                  disabled={isDeleting}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.deleteUser')}</AlertDialogTitle>
            <AlertDialogDescription>{t('users.deleteUserConfirmation')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('forms.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground">
              {t('forms.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
